<template>
  <b-modal :id="id" headerClass="indigo">
    <template v-slot:modal-title>
      <h3 class="modal-title full-bar-title">Add {{ groupName }}</h3>
    </template>
    <label class="text-md">{{ groupName }} Name (optional):</label>
    <input type="text" v-model="name" class="form-control" />
    <label class="text-md">{{ groupName }} Description (optional):</label>
    <input type="text" v-model="description" class="form-control" />
    <label class="text-md">{{ fs.roleName(role) }}s: </label>
    <div v-for="user in selectedUsers" :key="user.email">
      {{ user.email }}
    </div>
    <div
      v-if="pendingUsers.length > 0"
      style="margin: 20px; font-style: italic"
    >
      <label>Pending Invite:</label>
      <div v-for="user in pendingUsers" :key="user.email">
        {{ user.email }}
      </div>
    </div>
    <div class="demargin">
      <div class="row mx-0">
        <div class="col-12 pt-4 px-4">
          <label
            >Enter {{ fs.roleName(role) }} Email
            <span class="text-muted">or</span> Search by Name</label
          >
          <input
            type="text"
            v-model="filter"
            class="form-control"
            :placeholder="`Search ${fs.roleName(role)}s...`"
          />
        </div>
        <hr class="w-100" />
        <div class="px-4 w-100">
          <div
            v-for="user in searchedUsers"
            :key="user.id"
            class="card py-1 px-2 mb-1 d-flex flex-row align-items-center"
            :class="{ 'text-muted': user.existing }"
          >
            <div class="flex-grow">
              <div>{{ fs.displayName(user.name) }}</div>
              <div class="text-muted mb-1" style="line-height: 1">
                {{ user.email }}
              </div>
            </div>

            <button
              v-if="!alreadyAdded(user)"
              class="ml-3 mr-1 btn btn-no-border btn-si-green btn-si-icon"
              v-tippy
              title="Add"
              @click="pick(user)"
            >
              <i class="fas fa-plus" />
            </button>
            <a
              v-if="alreadyAdded(user)"
              class="ml-3 mr-1 btn btn-no-border btn-si-gray btn-si-icon text-white"
              v-tippy
              title="Already added"
            >
              <i class="fas fa-check" />
            </a>
          </div>

          <div
            v-if="promptInvite && !emailTaken"
            class="d-flex flex-column align-items-center"
          >
            <div class="text-md mt-2">There is no user with the email</div>
            <div class="text-lg si-text-blue">{{ filter }}</div>
            <div class="text-md">currently in STEM Innovator®</div>
            <button
              v-if="!alreadyAddedPending({ email: this.filter })"
              class="btn btn-no-border btn-si-green mt-3 mb-4"
              @click="addPending"
            >
              Add to invite list
            </button>
            <a
              v-if="alreadyAddedPending({ email: this.filter })"
              class="btn btn-no-border btn-si-gray mt-3 mb-4 disabled"
              style="color: #121212"
            >
              Added
            </a>
          </div>
          <div
            v-if="emailTaken"
            class="d-flex flex-column align-items-center"
          >
            <div class="text-md mt-2">A user with the email</div>
            <div class="text-lg si-text-blue">{{ filter }}</div>
            <div class="text-md">already exists in STEM Innovator®</div>
            <button class="btn btn-no-border btn-si-gray mt-3 mb-4" disabled>
              Already Exists
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="totalRows > perPage"
        class="p-1 b-t mt-2 white d-flex flex-row align-items-center"
      >
        <a class="flex mx-3"
          >Page {{ totalRows > 0 ? currentPage : 0 }} of
          {{ Math.ceil(totalRows / perPage) }} ({{ totalRows }})</a
        >
        <div>
          <b-pagination
            size="md"
            class="m-1"
            :totalRows="totalRows"
            v-model="currentPage"
            :perPage="perPage"
          ></b-pagination>
        </div>
      </div>
    </div>
    <template v-slot:modal-footer="{ _, cancel }">
      <button
        v-if="pendingUsers.length > 0"
        type="button"
        class="btn btn-primary"
        @click="save"
      >
        Save and Send Invites
      </button>
      <button
        v-if="pendingUsers.length <= 0"
        type="button"
        class="btn btn-primary"
        @click="save"
      >
        Save
      </button>
      <button type="button" class="btn" @click="cancel">Cancel</button>
    </template>
  </b-modal>
</template>

<script>
import SectionService from "@/services/SectionService";
import AdminService from "@/services/AdminService"
import Notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import _ from "lodash";

export default {
  name: "MultiUserSearchModal",

  props: {
    id: {
      type: String,
      default: "",
    },
    groupName: {
      type: String,
      default: "",
    },
    role: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      fs: fs,

      filter: null,
      searching: false,
      latestQuery: null,
      searchedUsers: null,

      name: null,
      description: null,
      selectedUsers: [],
      pendingUsers: [],

      perPage: 10,
      currentPage: 1,
      totalRows: 0,
      emailTaken: false,
    };
  },

  created() {
    this.search = _.debounce(this.doSearch, 250);
  },

  computed: {
    promptInvite() {
      if (!this.filter) return false;
      if (this.filter.includes(" ")) return false;
      if (!this.filter.includes("@")) return false;
      if (this.searching) return false;
      if (this.searchedUsers && this.searchedUsers.length > 0) return false;

      return true;
    },
  },

  watch: {
    filter() {
      this.search();
    },

    currentPage() {
      this.doSearch();
    },
  },

  methods: {
    doSearch() {
      let ctx = {
        page_size: this.perPage,
        current_page: this.currentPage,
      };

      this.searching = true;
      let timestamp = new Date().getTime();
      this.latestQuery = timestamp;

      SectionService.searchUsers(ctx, this.role, this.filter)
        .then((r) => {
          if (timestamp != this.latestQuery) {
            console.log("Query superseded".timestamp, this.latestQuery);
            return;
          }

          this.searching = false;
          this.searchedUsers = r.data.result;
          this.totalRows = r.data.count;

          if (this.existing) {
            let existingUsers = {};
            _.each(this.existing, (e) => {
              existingUsers[e.id] = true;
            });

            _.each(this.searchedUsers, (su) => {
              if (existingUsers[su.id]) {
                su.existing = true;
              }
            });
          }
        })
        .catch((e) => {
          this.searching = false;
          console.log(e);
          Notie.error("Failed to search users");
        });

      //check for users system wide
      AdminService.listUsers({ query: { email: this.filter } })
        .then((r) => {
          console.log(r.data.result);
          if (r.data.result.length > 0) {
            this.emailTaken = true;
          } else {
            this.emailTaken = false;
          }
        })
        .catch((e) => {
          console.log(e);
          Notie.error("Failed to search email", e);
        });
    },

    alreadyAdded(user) {
      for (var selectedUser of this.selectedUsers) {
        if (user.id == selectedUser.id) {
          return true;
        }
      }
      return false;
    },

    alreadyAddedPending(user) {
      for (var pendingUser of this.pendingUsers) {
        if (user.email == pendingUser.email) {
          return true;
        }
      }
      return false;
    },

    pick(user) {
      this.selectedUsers.push(user);
    },

    addPending() {
      this.pendingUsers.push({ email: this.filter });
    },

    save() {
      this.$emit("save", {
        name: this.name,
        description: this.description,
        selectedUsers: this.selectedUsers,
        pendingUsers: this.pendingUsers,
      });
      this.$bvModal.hide(this.id);

      this.filter = null;
      this.searchedUsers = null;
      this.name = null;
      this.description = null;
      this.selectedUsers = [];
      this.pendingUsers = [];

      return;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.demargin {
  margin: -1rem;
}
</style>
